import { GoalProgressCount } from '@griply/types';
import {
  buildGoalCategoryTree,
  formatDeadlineQuarterLabel,
  GoalCategory,
  GoalCategoryKind,
} from '@griply/utils';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { GoalGroupBy } from 'shared/types/sorting-mode';

import { Category } from './types';

export type BuildGoalTimelineCategoriesOptions = {
  goals: Goal[];
  // Unfiltered set: a sub-goal keeps its inherited life area when a filter drops its parent.
  allGoals: Goal[];
  lifeAreas: LifeArea[];
  groupBy?: GoalGroupBy;
  customOrder?: ID[];
  taskCounts?: Map<ID, number>;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  otherLifeArea: LifeArea;
  noDeadlineName: string;
};

const toTimelineCategory = (
  { id, kind, lifeArea, quarterKey, subCategories }: GoalCategory,
  otherLifeArea: LifeArea,
  noDeadlineName: string,
): Category => {
  if (kind === GoalCategoryKind.Quarter) {
    const name = formatDeadlineQuarterLabel(quarterKey ?? '') ?? noDeadlineName;
    return { id, lifeArea: { ...otherLifeArea, id, name }, subCategories };
  }

  if (kind === GoalCategoryKind.Ungrouped) {
    return { id, lifeArea: otherLifeArea, subCategories, hideHeader: true };
  }

  return { id, lifeArea: lifeArea ?? otherLifeArea, subCategories };
};

export const buildGoalTimelineCategories = ({
  goals,
  allGoals,
  lifeAreas,
  groupBy,
  customOrder,
  taskCounts,
  goalProgressCounts,
  otherLifeArea,
  noDeadlineName,
}: BuildGoalTimelineCategoriesOptions): Category[] =>
  buildGoalCategoryTree({
    goals,
    allGoals,
    lifeAreas,
    groupBy,
    customOrder,
    taskCounts,
    goalProgressCounts,
  }).map((category) =>
    toTimelineCategory(category, otherLifeArea, noDeadlineName),
  );
